import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import * as notificationService from '@/services/notificationService';
import { Notification as DbNotification } from '@/services/notificationService';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Query key factory for notifications
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  byUser: (userId: string) => ['notifications', userId] as const,
  unreadCount: (userId: string) => ['notifications', 'unread', userId] as const,
};

/**
 * Hook to fetch user notifications with realtime updates
 */
export function useUserNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: notificationKeys.byUser(user?.id || ''),
    queryFn: () => notificationService.getUserNotifications(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 30, // 30 seconds - notifications should be relatively fresh
  });

  // Set up realtime subscription for notifications
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          // Invalidate queries when notifications change
          queryClient.invalidateQueries({ queryKey: notificationKeys.byUser(user.id) });
          queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount(user.id) });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return query;
}

/**
 * Hook to get unread notification count
 */
export function useUnreadNotificationCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: notificationKeys.unreadCount(user?.id || ''),
    queryFn: () => notificationService.getUnreadNotificationCount(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook to mark notification as read
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: notificationService.markNotificationAsRead,
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.byUser(user?.id || '') });

      // Snapshot previous value
      const previousNotifications = queryClient.getQueryData(notificationKeys.byUser(user?.id || ''));

      // Optimistically update
      queryClient.setQueryData(
        notificationKeys.byUser(user?.id || ''),
        (old: DbNotification[] = []) => old.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );

      return { previousNotifications };
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(notificationKeys.byUser(user?.id || ''), context.previousNotifications);
      }
    },
    onSettled: () => {
      // Always refetch after mutation
      queryClient.invalidateQueries({ queryKey: notificationKeys.byUser(user?.id || '') });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount(user?.id || '') });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: () => notificationService.markAllNotificationsAsRead(user!.id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.byUser(user?.id || '') });

      const previousNotifications = queryClient.getQueryData(notificationKeys.byUser(user?.id || ''));

      queryClient.setQueryData(
        notificationKeys.byUser(user?.id || ''),
        (old: DbNotification[] = []) => old.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );

      return { previousNotifications };
    },
    onError: (err, _, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(notificationKeys.byUser(user?.id || ''), context.previousNotifications);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.byUser(user?.id || '') });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount(user?.id || '') });
    },
  });
}

/**
 * Hook to delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: notificationService.deleteNotification,
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.byUser(user?.id || '') });

      const previousNotifications = queryClient.getQueryData(notificationKeys.byUser(user?.id || ''));

      queryClient.setQueryData(
        notificationKeys.byUser(user?.id || ''),
        (old: DbNotification[] = []) => old.filter(n => n.id !== notificationId)
      );

      return { previousNotifications };
    },
    onError: (err, notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(notificationKeys.byUser(user?.id || ''), context.previousNotifications);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.byUser(user?.id || '') });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount(user?.id || '') });
    },
  });
}
